// noinspection SpellCheckingInspection

import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField from '@mui/material/TextField';
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import { addSpacesToTitleCase } from "common/helpers/utils";
import { DocumentQuery } from "documents/entities/document/api/document-api-service";
import DocumentParameters, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters
} from "documents/entities/document/api/request-contracts/document-parameters";
import Document from "documents/entities/document/document";
import {
  getDocumentsByQuery,
  getDocumentsQueryByInterface,
  getIsLoadingDocumentsByQuery,
  populateDocumentsByQuery,
  updateDocumentsInterfaceQuery
} from "documents/entities/document/store/documents-redux-slice";
import EntityMember from "legal-entities/entities/entity-member/entity-member";
import {
  getEntityMembers,
  populateEntityMembers
} from "legal-entities/entities/entity-member/store/entity-members-redux-slice";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import { ChildResourceType, ResourceCategory } from "permissions/entities/permission/resources/general";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/session/session-context";
import ProposalParameters, {
  ProposalBoxType,
  ProposalContext,
  ProposalFilterParameters,
  ProposalOrderField,
  ProposalOrderParameters
} from "work/entities/proposal/api/request-contracts/proposal-parameters";
import Proposal from "work/entities/proposal/proposal";
import {
  getIsLoadingProposalsByQuery,
  getProposalQueryByInterface,
  getProposalsByQuery,
  populateProposalsByQuery,
  ProposalQuery,
  updateProposalInterfaceQuery
} from "work/entities/proposal/store/proposals-redux-slice";
import WorkAgreementParameters, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderField,
  WorkAgreementOrderParameters
} from "work/entities/work-agreement/api/request-contracts/work-agreement-parameters";
import {
  getIsLoadingWorkAgreementsByQuery,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  populateWorkAgreementsByQuery,
  updateWorkAgreementInterfaceQuery,
  WorkAgreementQuery
} from "work/entities/work-agreement/store/work-agreement-redux-slice";
import WorkAgreement from "work/entities/work-agreement/work-agreement";

const Drawer = styled(MuiDrawer)(() => ({
  boxSizing: 'border-box',
  flexShrink: 0,
  whiteSpace: 'nowrap'
}),
);
const PermissionsFilterPanel = styled(
  Drawer,
  { shouldForwardProp: (prop) => prop !== "closeAfterTransition" }
)(
  ({ theme }) => ({
    position: "fixed",
    zIndex: theme.zIndex.modal - 1,
  }));
const PermissionsFilterPanelContainer = styled("div")(
  () => ({
    flex: 1,
    height: "100%",
  }));
const PermissionsFilterPanelContent = styled("div")(
  ({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
    width: "400px",
  }));
const TitleBar = styled("div")(
  ({ theme }) => ({
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    paddingBottom: theme.spacing(1),
  }));
const ActionButtons = styled("div")(
  ({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    width: '100%'
  }));
const AccessTypeContainer = styled("div")(
  ({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  }));

type PermissionsFilterDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onFilter: () => void;
};

export default function PermissionsFilterDrawer(props: Readonly<PermissionsFilterDrawerProps>) {
  const {
    isOpen,
    onClose,
    onFilter
  } = props;

  // General State
  const [hasParent, setHasParent] = useState<boolean | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<EntityMember[]>([]);
  const [selectedAccessType, setSelectedAccessType] = useState<string | null>(null);
  const [filtersChanged, setFiltersChanged] = useState(false);

  // Documents State
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);

  // Proposals State
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [selectedProposals, setSelectedProposals] = useState<Proposal[]>([]);

  // Work Agreements State
  const [workAgreements, setWorkAgreements] = useState<WorkAgreement[]>([]);
  const [selectedWorkAgreements, setSelectedWorkAgreements] = useState<WorkAgreement[]>([]);

  // Hooks
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAttorneyHubDispatch();

  // Entity Members
  const entityMembers = getEntityMembers(session.currentEntity.entityId);

  // Documents Query
  const documentsInterfaceName = "contractsIndex";
  const documentQuery = getDocumentsQueryByInterface(documentsInterfaceName);
  const documentsResponse = getDocumentsByQuery(documentQuery);
  const documentsLoading = getIsLoadingDocumentsByQuery(documentQuery);

  // Proposals Query
  const proposalsInterfaceName = "proposalsIndex";
  const proposalQuery = getProposalQueryByInterface(proposalsInterfaceName);
  const proposalsResponse = getProposalsByQuery(proposalQuery);
  const proposalsLoading = getIsLoadingProposalsByQuery(proposalQuery);

  // Work Agreements Query
  const workAgreementsInterfaceName = "workAgreementIndex";
  const workAgreementQuery = getWorkAgreementQueryByInterface(workAgreementsInterfaceName);
  const workAgreementsResponse = getWorkAgreementsByQuery(workAgreementQuery);
  const workAgreementsLoading = getIsLoadingWorkAgreementsByQuery(workAgreementQuery);

  useEffect(() => {
    populateUsers();
  }, []);

  useEffect(() => {
    if (isOpen) {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('hasParent') !== 'true') {
        searchParams.set('hasParent', 'true');
        navigate({ search: searchParams.toString() });
      }
    }
  }, [
    isOpen, 
    location.search, 
    navigate
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasParent = searchParams.get('hasParent');
    const categories = searchParams.getAll('categories');
    const granteeUserIds = searchParams.getAll('granteeUserIds');
    const accessType = searchParams.get('accessType');
    const documentIds = searchParams.getAll('documentIds');
    const proposalIds = searchParams.getAll('proposalIds');
    const workAgreementIds = searchParams.getAll('workAgreementIds');

    setSelectedCategories(categories);
    setHasParent(hasParent === 'true');
    setSelectedUsers(
      granteeUserIds
        .map(userId => entityMembers?.find(member => member.userId.value === userId))
        .filter((member): member is EntityMember => member !== undefined)
    );
    setSelectedAccessType(accessType);
    setSelectedDocuments(
      documentIds
        .map(docId => documents.find(doc => doc.id.value === docId))
        .filter((doc): doc is Document => doc !== undefined)
    );
    setSelectedProposals(
      proposalIds
        .map(propId => proposals.find(prop => prop.id?.value === propId))
        .filter((prop): prop is Proposal => prop !== undefined)
    );
    setSelectedWorkAgreements(
      workAgreementIds
        .map(waId => workAgreements.find(wa => wa.id?.value === waId))
        .filter((wa): wa is WorkAgreement => wa !== undefined)
    );
  }, [
    location.search,
    entityMembers
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categories = searchParams.getAll('categories');
    const granteeUserIds = searchParams.getAll('granteeUserIds');
    const accessType = searchParams.get('accessType');
    const documentIds = searchParams.getAll('documentIds');
    const proposalIds = searchParams.getAll('proposalIds');
    const workAgreementIds = searchParams.getAll('workAgreementIds');

    const currentFilters = {
      hasParent,
      categories: selectedCategories,
      granteeUserIds: selectedUsers.map(u => u.userId.value),
      accessType: selectedAccessType,
      documentIds: selectedDocuments.map(d => d.id.value),
      proposalIds: selectedProposals.map(p => p.id?.value),
      workAgreementIds: selectedWorkAgreements.map(wa => wa.id?.value),
    };

    const urlFilters = {
      hasParent,
      categories,
      granteeUserIds,
      accessType,
      documentIds,
      proposalIds,
      workAgreementIds
    };

    // Compare JSON stringified values for simplicity
    setFiltersChanged(JSON.stringify(currentFilters) !== JSON.stringify(urlFilters));
  }, [
    location.search,
    selectedCategories,
    selectedUsers,
    selectedAccessType,
    selectedDocuments,
    selectedProposals,
    selectedWorkAgreements
  ]);

  useEffect(
    () => {
      if (!documentQuery) {
        dispatch(updateDocumentsInterfaceQuery({
          interfaceName: documentsInterfaceName,
          query: new DocumentQuery(
            new DocumentParameters(
              new DocumentOrderParameters(
                DocumentOrderField.Name,
                OrderDirection.Ascending
              ),
              new DocumentFilterParameters()
            ),
            new PaginationParameters(0, 25)
          )
        }));
        return;
      }

      if (documentQuery && documentsResponse === undefined && !documentsLoading) {
        dispatch(
          populateDocumentsByQuery({
            session: session,
            query: documentQuery,
          })
        );
      } else if (documentsResponse !== undefined) {
        setDocuments(documentsResponse.data);
      }
    },
    [
      documentsResponse,
      documentQuery
    ]
  );

  useEffect(
    () => {
      if (!session.user?.companyEntityId) return;
      if (!proposalQuery) {
        dispatch(updateProposalInterfaceQuery({
          interfaceName: proposalsInterfaceName,
          query: new ProposalQuery(
            new ProposalParameters(
              new ProposalFilterParameters({
                context: session.context?.viewingAsVendor
                  ? ProposalContext.Vendor
                  : ProposalContext.Client,
                boxType: ProposalBoxType.AllActive
              }),
              new ProposalOrderParameters(
                ProposalOrderField.Created,
                OrderDirection.Descending
              )
            ),
            new PaginationParameters(0, 25)
          )
        }));
        return;
      }

      if (proposalQuery && proposalsResponse === undefined && !proposalsLoading) {
        dispatch(
          populateProposalsByQuery({
            session: session,
            query: proposalQuery,
          })
        );
      } else if (proposalsResponse !== undefined) {
        setProposals(proposalsResponse.data);
      }
    },
    [
      proposalsResponse,
      proposalQuery
    ]
  );

  useEffect(
    () => {
      if (!session.user?.companyEntityId) return;
      if (!workAgreementQuery) {
        dispatch(updateWorkAgreementInterfaceQuery({
          interfaceName: workAgreementsInterfaceName,
          query: new WorkAgreementQuery(
            new WorkAgreementParameters(
              new WorkAgreementFilterParameters(),
              new WorkAgreementOrderParameters(
                WorkAgreementOrderField.Start,
                OrderDirection.Descending
              )
            ),
            new PaginationParameters(0, 25)
          )
        }));
        return;
      }

      if (workAgreementQuery && workAgreementsResponse === undefined && !workAgreementsLoading) {
        dispatch(
          populateWorkAgreementsByQuery({
            session: session,
            query: workAgreementQuery,
          })
        );
      } else if (workAgreementsResponse !== undefined) {
        setWorkAgreements(workAgreementsResponse.data);
      }
    },
    [
      workAgreementsResponse,
      workAgreementQuery
    ]
  );

  function populateUsers() {
    dispatch(populateEntityMembers({
      session: session,
      entity: new LegalEntity(session.currentEntity.entityId)
    }));
  }

  function handleFilterClicked() {
    const filters = {
      categories: selectedCategories,
      granteeUserIds: selectedUsers.map(entityMember => entityMember.userId.value),
      accessType: selectedAccessType,
      documentIds: selectedDocuments.map(doc => doc.id.value),
      proposalIds: selectedProposals.map(prop => prop.id?.value),
      workAgreementIds: selectedWorkAgreements.map(wa => wa.id?.value),
      hasParent: true
    };

    // Update URL search params with new filters
    const searchParams = new URLSearchParams();
    if (filters.hasParent) searchParams.append('hasParent', String(filters.hasParent));
    filters.categories.forEach(c => searchParams.append('categories', c));
    filters.granteeUserIds.forEach(u => searchParams.append('granteeUserIds', u));
    if (filters.accessType) searchParams.append('accessType', filters.accessType);
    filters.documentIds.forEach(d => searchParams.append('documentIds', d));
    filters.proposalIds.forEach(p => searchParams.append('proposalIds', p ?? ''));
    filters.workAgreementIds.forEach(wa => searchParams.append('workAgreementIds', wa ?? ''));

    navigate({ search: searchParams.toString() });

    onFilter();
  }

  function handleClearFilters() {
    const searchParams = new URLSearchParams();
    navigate({ search: searchParams.toString() });

    setHasParent(null);
    setSelectedCategories([]);
    setSelectedUsers([]);
    setSelectedAccessType(null);
    setSelectedDocuments([]);
    setSelectedProposals([]);
    setSelectedWorkAgreements([]);

    onFilter();
  }

  return (
    <PermissionsFilterPanel
      open={isOpen}
      anchor="right"
      variant="temporary"
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
    >
      <PermissionsFilterPanelContainer>
        <PermissionsFilterPanelContent>
          <TitleBar>
            <Typography variant="h5">
              Filter Additional Permissions
            </Typography>
            <Tooltip title="Close panel">
              <IconButton
                size="medium"
                onClick={onClose}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          <Stack gap={3} marginTop={2}>
            <Autocomplete
              multiple
              options={
                Object.keys(ResourceCategory).filter(key => Number.isNaN(Number(key)))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categories"
                  placeholder="Search For Category"
                />
              )}
              value={selectedCategories}
              onChange={(_event, newValue) => {
                setSelectedCategories(newValue);
              }}
            />
            <Autocomplete
              multiple
              options={entityMembers}
              getOptionLabel={(option) => option.name.toString()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Users"
                  placeholder="Search For User"
                />
              )}
              value={selectedUsers}
              onChange={(_event, newValue) => {
                setSelectedUsers(newValue);
              }}
            />
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Access Type
                </Typography>
                <AccessTypeContainer>
                  <Autocomplete
                    options={
                      Object.keys(ChildResourceType)
                        .filter(key => Number.isNaN(Number(key)))
                        .map(key => addSpacesToTitleCase(key.replace('Access', '')))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter By Access Type"
                        placeholder="Search For Access Type"
                      />
                    )}
                    value={selectedAccessType}
                    onChange={(_event, newValue) => {
                      setSelectedAccessType(newValue);
                    }}
                  />
                  {selectedAccessType === 'Document' && (
                    <Autocomplete
                      multiple
                      options={documents}
                      getOptionLabel={(option) => option.name?.toString() ?? 'Unknown Document'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={selectedAccessType ? `Filter By ${selectedAccessType}` : ""}
                          placeholder={
                            selectedAccessType
                              ? "Search For Document"
                              : "Select an access type above"
                          }
                        />
                      )}
                      value={selectedDocuments}
                      onChange={(_event, newValue) => {
                        setSelectedDocuments(newValue);
                      }}
                    />
                  )}
                  {selectedAccessType === 'Proposal' && (
                    <Autocomplete
                      multiple
                      options={proposals}
                      getOptionLabel={(option) => option.name?.toString() ?? 'Unknown Proposal'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={selectedAccessType ? `Filter By ${selectedAccessType}` : ""}
                          placeholder={
                            selectedAccessType
                              ? "Search For Proposal"
                              : "Select an access type above"
                          }
                        />
                      )}
                      value={selectedProposals}
                      onChange={(_event, newValue) => {
                        setSelectedProposals(newValue);
                      }}
                    />
                  )}
                  {selectedAccessType === 'Work Agreement' && (
                    <Autocomplete
                      multiple
                      options={workAgreements}
                      getOptionLabel={(option) => option.name?.toString() ?? 'Unknown Work Agreement'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={selectedAccessType ? `Filter By ${selectedAccessType}` : ""}
                          placeholder={
                            selectedAccessType
                              ? "Search For Work Agreement"
                              : "Select an access type above"
                          }
                        />
                      )}
                      value={selectedWorkAgreements}
                      onChange={(_event, newValue) => {
                        setSelectedWorkAgreements(newValue);
                      }}
                    />
                  )}
                </AccessTypeContainer>
              </CardContent>
            </Card>
            <ActionButtons>
              <Button
                variant="contained"
                color="primary"
                disabled={!filtersChanged}
                startIcon={<FilterListIcon />}
                onClick={handleFilterClicked}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ClearIcon />}
                disabled={
                  !selectedCategories.length &&
                  !selectedUsers.length &&
                  !selectedAccessType &&
                  !selectedDocuments.length &&
                  !selectedProposals.length &&
                  !selectedWorkAgreements.length
                }
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </ActionButtons>
          </Stack>
        </PermissionsFilterPanelContent>
      </PermissionsFilterPanelContainer>
    </PermissionsFilterPanel>
  );
}
